import type {ConversationMessage} from "@/services/wingman";

export const TICKET_CONFIRMATION_CHOICE_KINDS = [
   "create_yes",
   "create_no",
   "comment_yes",
   "comment_no",
   "create_force_new",
   "decline",
] as const;

export type TicketConfirmationChoiceKind =
   (typeof TICKET_CONFIRMATION_CHOICE_KINDS)[number];

export type TicketConfirmationChoice = {
   id: string;
   kind: TicketConfirmationChoiceKind;
   userText: string;
   ticketNumber?: number;
};

const TICKET_CONFIRM_TOOLS = new Set(["create_support_ticket", "add_ticket_comment"]);

const KIND_SET = new Set<string>(TICKET_CONFIRMATION_CHOICE_KINDS);

const isRecord = (value: unknown): value is Record<string, unknown> =>
   value != null && typeof value === "object" && !Array.isArray(value);

const parseResultPayload = (result: unknown): unknown => {
   if (typeof result !== "string") {
      return result;
   }
   try {
      return JSON.parse(result);
   } catch {
      return null;
   }
};

const parseChoice = (value: unknown): TicketConfirmationChoice | null => {
   if (!isRecord(value)) {
      return null;
   }
   if (typeof value.id !== "string" || value.id.length === 0) {
      return null;
   }
   if (typeof value.kind !== "string" || !KIND_SET.has(value.kind)) {
      return null;
   }
   if (typeof value.userText !== "string" || value.userText.length === 0) {
      return null;
   }
   const ticketNumber =
      typeof value.ticketNumber === "number" && Number.isInteger(value.ticketNumber)
         ? value.ticketNumber
         : undefined;
   return {
      id: value.id,
      kind: value.kind as TicketConfirmationChoiceKind,
      userText: value.userText,
      ...(ticketNumber != null ? {ticketNumber} : {}),
   };
};

export const parseTicketConfirmationChoices = (
   result: unknown
): TicketConfirmationChoice[] | null => {
   const payload = parseResultPayload(result);
   if (!isRecord(payload) || !Array.isArray(payload.choices)) {
      return null;
   }
   const choices = payload.choices
      .map(parseChoice)
      .filter((choice): choice is TicketConfirmationChoice => choice != null);
   return choices.length > 0 ? choices : null;
};

export const hasRelatedTicketMatches = (result: unknown): boolean => {
   const payload = parseResultPayload(result);
   return isRecord(payload) && Array.isArray(payload.matches) && payload.matches.length > 0;
};

export const ticketConfirmationChoicesFromContents = (
   contents: ConversationMessage["contents"]
): TicketConfirmationChoice[] | null => {
   let found: TicketConfirmationChoice[] | null = null;
   for (const item of contents) {
      if (item.type !== "function_result") {
         continue;
      }
      if (!TICKET_CONFIRM_TOOLS.has(item.functionName)) {
         continue;
      }
      const parsed = parseTicketConfirmationChoices(item.result);
      if (parsed) {
         found = parsed;
      }
   }
   return found;
};

export type TicketConfirmFunctionPart = {
   type: string;
   data?: {functionName?: string};
   result?: unknown;
};

export const ticketConfirmationChoicesFromParts = (
   parts: ReadonlyArray<TicketConfirmFunctionPart>
): TicketConfirmationChoice[] | null => {
   let found: TicketConfirmationChoice[] | null = null;
   for (const part of parts) {
      if (part.type !== "functionCall") {
         continue;
      }
      const functionName = part.data?.functionName;
      if (functionName == null || !TICKET_CONFIRM_TOOLS.has(functionName)) {
         continue;
      }
      const parsed = parseTicketConfirmationChoices(part.result);
      if (parsed) {
         found = parsed;
      }
   }
   return found;
};

type TicketConfirmTranslate = (key: string, options?: {number?: number}) => string;

export const ticketConfirmationSuggestionLabel = (
   choice: TicketConfirmationChoice,
   t: TicketConfirmTranslate
): string => {
   switch (choice.kind) {
      case "create_yes":
         return t("ticketConfirm.createYes");
      case "comment_yes":
         return choice.ticketNumber != null
            ? t("ticketConfirm.commentYes", {number: choice.ticketNumber})
            : t("ticketConfirm.commentYesGeneric");
      case "create_force_new":
         return t("ticketConfirm.fileNew");
      case "create_no":
      case "comment_no":
      case "decline":
         return t("ticketConfirm.no");
   }
};

export const pylonSupportCompletedPhraseKey = (
   functionName: string,
   result: unknown
): string | null => {
   if (functionName === "create_support_ticket" && hasRelatedTicketMatches(result)) {
      return "tools.phrases.foundRelatedTicket";
   }
   if (
      TICKET_CONFIRM_TOOLS.has(functionName) &&
      parseTicketConfirmationChoices(result) != null
   ) {
      return "tools.phrases.askedAboutTicket";
   }
   return null;
};
